import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, X, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import api from '../../lib/api';

/**
 * Iter 286.w — Banner de alerta de Pagos Anulados.
 *
 * Consulta `/flujo-caja/pagos-anulados-alerta` y, si hay facturas revertidas
 * por payments anulados o bills reabiertas en Alegra dentro de los últimos
 * `dias` días, muestra un banner amarillo con el detalle. El usuario puede:
 *   - Expandir la lista de facturas afectadas.
 *   - Cerrar el banner hasta que aparezca una revertida nueva (dismiss
 *     persistido en localStorage, se compara contra el `reabierta_at` más
 *     reciente).
 */
const fmtCLP = (v) => `$${Math.round(Number(v) || 0).toLocaleString('es-CL')}`;
const fmtFechaHora = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso.slice(0, 16).replace('T', ' ');
  }
};

const DISMISS_KEY = 'fc-pagos-anulados-dismissed-until';

const motivoLabel = (m) => {
  if (!m) return 'Reabierta';
  if (m.startsWith('alegra_payment_') && m.endsWith('_voided')) return 'Pago anulado en Alegra';
  if (m === 'bill_reabierta_en_alegra') return 'Factura reabierta en Alegra';
  if (m === 'bill_eliminada_en_alegra') return 'Factura eliminada en Alegra';
  return m;
};

export default function PagosAnuladosBanner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(() => localStorage.getItem(DISMISS_KEY) || '');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/flujo-caja/pagos-anulados-alerta?dias=30');
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data || !data.items || data.items.length === 0) return null;

  // Regla dismiss: si el usuario cerró el banner y no hay ninguna revertida MÁS NUEVA
  // que ese timestamp, no mostrar. Si aparece una nueva → banner vuelve a aparecer.
  const latestReabierta = data.items[0]?.reabierta_at || '';
  if (dismissedAt && latestReabierta && latestReabierta <= dismissedAt) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, latestReabierta);
    setDismissedAt(latestReabierta);
  };

  return (
    <div
      className="rounded-lg border-2 border-amber-400 bg-amber-50 shadow-sm"
      data-testid="pagos-anulados-banner"
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="mt-0.5 shrink-0">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-semibold text-amber-900" data-testid="pagos-anulados-title">
              Pagos anulados detectados en Alegra
            </div>
            <Badge variant="outline" className="bg-white text-amber-900 border-amber-400" data-testid="pagos-anulados-count">
              {data.total} factura{data.total !== 1 ? 's' : ''}
            </Badge>
            <Badge variant="outline" className="bg-white text-amber-900 border-amber-400" data-testid="pagos-anulados-monto">
              {fmtCLP(data.monto_total)}
            </Badge>
          </div>
          <div className="text-xs text-amber-800 mt-1">
            El reconciliador diario detectó pagos que fueron anulados en Alegra. Las facturas volvieron a{' '}
            <b>Por Pagar</b> automáticamente. Revísalas y confirma si necesitan re-generar el pago.
            {data.ultima_corrida_reconciliador && (
              <span className="text-[11px] text-amber-700/80 ml-1">
                (última verificación: {fmtFechaHora(data.ultima_corrida_reconciliador)})
              </span>
            )}
          </div>

          {expanded && (
            <div className="mt-3 border-t border-amber-300 pt-2 space-y-1.5" data-testid="pagos-anulados-list">
              {data.items.map((it) => (
                <div
                  key={it.id}
                  className="flex flex-wrap items-center gap-2 text-xs bg-white/70 rounded px-2 py-1.5 border border-amber-200"
                  data-testid={`pago-anulado-row-${it.folio}`}
                >
                  <span className="font-mono font-semibold text-amber-900">#{it.folio}</span>
                  <span className="text-slate-700 truncate max-w-[240px]">{it.nombre_proveedor || '—'}</span>
                  <span className="font-medium text-slate-900">{fmtCLP(it.monto)}</span>
                  <span className="text-slate-500 text-[11px]">{motivoLabel(it.reabierta_motivo)}</span>
                  {it.void_alegra_payment_id && (
                    <a
                      href={`https://app.alegra.com/payment/view/id/${it.void_alegra_payment_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-blue-600 hover:underline text-[11px]"
                    >
                      Pago #{it.void_alegra_payment_id} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <span className="text-slate-500 text-[11px] ml-auto">{fmtFechaHora(it.reabierta_at)}</span>
                </div>
              ))}
              <div className="text-[11px] text-amber-800/80 pt-1">
                Estas facturas ya aparecen en <b>Por Pagar</b> del panel CxP. Al pagarlas nuevamente se sincronizarán con Alegra.
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs bg-white hover:bg-amber-100 border-amber-400 text-amber-900"
            onClick={() => setExpanded((x) => !x)}
            data-testid="pagos-anulados-toggle"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
            {expanded ? 'Ocultar' : 'Ver detalles'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs hover:bg-amber-100 text-amber-900"
            onClick={load}
            title="Refrescar"
            data-testid="pagos-anulados-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 hover:bg-amber-100 text-amber-900"
            onClick={handleDismiss}
            title="Cerrar hasta la próxima anulación"
            data-testid="pagos-anulados-dismiss"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
